// In-memory mock database. Persists for the session via simple module state.
// Used by RTK Query mock baseQuery to simulate REST endpoints.

export type ID = string;

export interface AcademicYear {
  id: ID;
  name: string;
  description: string;
  status: "active" | "inactive";
  createdAt: string;
}
export interface Subject {
  id: ID;
  name: string;
  yearId: ID;
  description: string;
  status: "active" | "inactive";
  createdAt: string;
}
export interface Chapter {
  id: ID;
  name: string;
  yearId: ID;
  subjectId: ID;
  number: number;
  description: string;
  createdAt: string;
}
export interface Student {
  id: ID;
  name: string;
  email: string;
  mobile: string;
  yearId: ID;
  enrollmentDate: string;
  status: "active" | "inactive";
}
export interface Video {
  id: ID;
  title: string;
  yearId: ID;
  subjectId: ID;
  chapterId: ID;
  url: string;
  thumbnail: string;
  duration: string;
  type: "free" | "paid";
  createdAt: string;
}
export interface Note {
  id: ID;
  title: string;
  yearId: ID;
  subjectId: ID;
  chapterId: ID;
  fileName: string;
  pdfType: "demo" | "paid";
  description: string;
  createdAt: string;
}
export interface ChapterTest {
  id: ID;
  title: string;
  yearId: ID;
  subjectId: ID;
  chapterId: ID;
  duration: number;
  totalMarks: number;
  passingMarks: number;
  status: "active" | "draft";
  createdAt: string;
}
export interface WeeklyExam {
  id: ID;
  title: string;
  yearId: ID;
  subjectId: ID;
  chapterIds: ID[];
  weekNumber: number;
  duration: number;
  totalMarks: number;
  passingMarks: number;
  startDate: string;
  endDate: string;
  status: "active" | "draft";
  createdAt: string;
}
export interface Question {
  id: ID;
  text: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correct: "A" | "B" | "C" | "D";
  explanation: string;
  marks: number;
  difficulty: "easy" | "medium" | "hard";
  yearId: ID;
  subjectId: ID;
  chapterId: ID;
  // Linkage: bank only, or attached to a chapterTest / weeklyExam
  source: "bank" | "chapterTest" | "weeklyExam";
  testId?: ID;
}

export const uid = () => Math.random().toString(36).slice(2, 10);
const now = () => new Date().toISOString();
const daysAgo = (n: number) =>
  new Date(Date.now() - n * 86400000).toISOString();

export const db = {
  years: [] as AcademicYear[],
  subjects: [] as Subject[],
  chapters: [] as Chapter[],
  students: [] as Student[],
  videos: [] as Video[],
  notes: [] as Note[],
  chapterTests: [] as ChapterTest[],
  weeklyExams: [] as WeeklyExam[],
  questions: [] as Question[],
};

// ---- Seed ----
const seed = () => {
  const y1 = { id: uid(), name: "First Year", description: "MBBS Year 1", status: "active" as const, createdAt: daysAgo(120) };
  const y2 = { id: uid(), name: "Second Year", description: "MBBS Year 2", status: "active" as const, createdAt: daysAgo(110) };
  const y3 = { id: uid(), name: "Third Year", description: "MBBS Year 3", status: "active" as const, createdAt: daysAgo(100) };
  const y4 = { id: uid(), name: "Final Year", description: "MBBS Final", status: "active" as const, createdAt: daysAgo(90) };
  db.years.push(y1, y2, y3, y4);

  const subjectsByYear: Record<string, string[]> = {
    [y1.id]: ["Anatomy", "Physiology", "Biochemistry"],
    [y2.id]: ["Pathology", "Microbiology", "Pharmacology"],
    [y3.id]: ["Medicine", "Surgery", "ENT"],
    [y4.id]: ["Obs & Gyn", "Pediatrics", "Orthopedics"],
  };
  Object.entries(subjectsByYear).forEach(([yearId, names]) => {
    names.forEach((n) =>
      db.subjects.push({
        id: uid(),
        name: n,
        yearId,
        description: `${n} subject`,
        status: "active",
        createdAt: daysAgo(80),
      })
    );
  });

  db.subjects.forEach((s) => {
    for (let i = 1; i <= 5; i++) {
      db.chapters.push({
        id: uid(),
        name: `${s.name} Chapter ${i}`,
        yearId: s.yearId,
        subjectId: s.id,
        number: i,
        description: `Chapter ${i} of ${s.name}`,
        createdAt: daysAgo(60 - i),
      });
    }
  });

  // Students
  const firstNames = ["Aarav", "Vivaan", "Aditya", "Vihaan", "Arjun", "Sai", "Reyansh", "Krishna", "Ishaan", "Rohan", "Diya", "Ananya", "Aadhya", "Saanvi", "Pari"];
  const lastNames = ["Sharma", "Verma", "Patel", "Gupta", "Singh", "Kumar", "Reddy", "Mehta", "Nair", "Iyer"];
  for (let i = 0; i < 48; i++) {
    const fn = firstNames[i % firstNames.length];
    const ln = lastNames[i % lastNames.length];
    db.students.push({
      id: uid(),
      name: `${fn} ${ln}`,
      email: `${fn.toLowerCase()}.${ln.toLowerCase()}${i}@medu.app`,
      mobile: `+91 9${(800000000 + i * 137).toString().slice(0, 9)}`,
      yearId: db.years[i % 4].id,
      enrollmentDate: daysAgo(i * 2),
      status: i % 7 === 0 ? "inactive" : "active",
    });
  }

  // Videos & notes per chapter (sample)
  db.chapters.slice(0, 18).forEach((c, i) => {
    db.videos.push({
      id: uid(),
      title: `${c.name} - Lecture`,
      yearId: c.yearId,
      subjectId: c.subjectId,
      chapterId: c.id,
      url: "https://example.com/video.mp4",
      thumbnail: "",
      duration: `${20 + (i % 30)}:00`,
      type: i % 3 === 0 ? "free" : "paid",
      createdAt: daysAgo(40 - i),
    });
    db.notes.push({
      id: uid(),
      title: `${c.name} - Notes`,
      yearId: c.yearId,
      subjectId: c.subjectId,
      chapterId: c.id,
      fileName: `${c.name.replace(/\s+/g, "_").toLowerCase()}.pdf`,
      pdfType: i % 2 === 0 ? "demo" : "paid",
      description: "Lecture notes",
      createdAt: daysAgo(35 - i),
    });
  });

  // Chapter tests
  db.chapters.slice(0, 10).forEach((c, i) => {
    const t: ChapterTest = {
      id: uid(),
      title: `${c.name} Test`,
      yearId: c.yearId,
      subjectId: c.subjectId,
      chapterId: c.id,
      duration: 30,
      totalMarks: 50,
      passingMarks: 20,
      status: i % 4 === 0 ? "draft" : "active",
      createdAt: daysAgo(20 - i),
    };
    db.chapterTests.push(t);
    for (let q = 0; q < 5; q++) {
      db.questions.push({
        id: uid(),
        text: `Sample question ${q + 1} for ${c.name}?`,
        optionA: "Option A",
        optionB: "Option B",
        optionC: "Option C",
        optionD: "Option D",
        correct: ["A", "B", "C", "D"][q % 4] as any,
        explanation: "Because this is the right answer.",
        marks: 2,
        difficulty: ["easy", "medium", "hard"][q % 3] as any,
        yearId: c.yearId,
        subjectId: c.subjectId,
        chapterId: c.id,
        source: "chapterTest",
        testId: t.id,
      });
    }
  });

  // Weekly exams
  for (let w = 1; w <= 4; w++) {
    const y = db.years[w % 4];
    const subjs = db.subjects.filter((s) => s.yearId === y.id);
    const chs = db.chapters.filter((c) => c.subjectId === subjs[0]?.id).slice(0, 3);
    const we: WeeklyExam = {
      id: uid(),
      title: `Week ${w} Exam`,
      yearId: y.id,
      subjectId: subjs[0]?.id ?? "",
      chapterIds: chs.map((c) => c.id),
      weekNumber: w,
      duration: 60,
      totalMarks: 100,
      passingMarks: 40,
      startDate: daysAgo(10 - w),
      endDate: daysAgo(3 - w),
      status: "active",
      createdAt: daysAgo(15 - w),
    };
    db.weeklyExams.push(we);
  }

  // Question bank extras
  db.chapters.forEach((c, idx) => {
    if (idx % 3 !== 0) return;
    db.questions.push({
      id: uid(),
      text: `Bank question for ${c.name}?`,
      optionA: "A",
      optionB: "B",
      optionC: "C",
      optionD: "D",
      correct: "B",
      explanation: "Answer explanation",
      marks: 1,
      difficulty: "medium",
      yearId: c.yearId,
      subjectId: c.subjectId,
      chapterId: c.id,
      source: "bank",
    });
  });
};
seed();

// ---- Helpers used by mock baseQuery ----
export const paginate = <T,>(rows: T[], page = 1, limit = 10) => {
  const start = (page - 1) * limit;
  return { data: rows.slice(start, start + limit), total: rows.length, page, limit };
};

export const sortRows = <T extends Record<string, any>>(
  rows: T[],
  sortBy?: string,
  order: "asc" | "desc" = "desc"
) => {
  if (!sortBy) return rows;
  return [...rows].sort((a, b) => {
    const av = a[sortBy];
    const bv = b[sortBy];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return order === "asc" ? -1 : 1;
    if (av > bv) return order === "asc" ? 1 : -1;
    return 0;
  });
};

export const searchRows = <T,>(rows: T[], q: string | undefined, fields: (keyof T)[]) => {
  if (!q) return rows;
  const needle = q.toLowerCase();
  return rows.filter((r) => fields.some((f) => String(r[f] ?? "").toLowerCase().includes(needle)));
};
